import type { Stage} from "whole-shared";
import { logger,RawWSRequest, WSRequest } from "whole-shared";

import { config, EventEmitter } from "./config";
import { port } from "./env";

type WSStatus = "connecting" | "connected" | "idle" | "stop";

type WSEvents = {
  connection: [connected: boolean];
  inQueue: [inQueue: boolean];
  match: [matchId: string];
  update: [update: object];
};

class WS extends EventEmitter<WSEvents> {
  private clientId = "";
  private handlers = new Map<number, (request: WSRequest) => void>();
  private neverRetry = false;
  private playerId = -1;
  private requestNo = 0;
  private stage: Stage = "unknown";
  private status: WSStatus = "idle";
  private ws: WebSocket | null = null;

  constructor() {
    super();

    config.on("config", ({ clientId, player, stage }) => {
      if(this.playerId !== player.id && this.status === "connected") this.ws?.close();

      this.clientId = clientId;
      this.playerId = player.id;
      this.stage = stage;
      this.connect();
    });
  }

  private connect() {
    if(this.status !== "idle") return;

    this.status = "connecting";

    try {
      const { stage } = this;

      if(stage === "unknown") throw new Error("Can't connect with unknown stage");

      const ws = new WebSocket(
        {
          dev:  `ws://localhost:${port}/`,
          prod: "wss://test.wage-war.trinityteam.it/ws?transport=websocket",
          test: "wss://test.wage-war.trinityteam.it/ws?transport=websocket"
        }[stage]
      );

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const message = RawWSRequest.parse(event.data);
          const { answerTo, doubleConnection, queue, update, ...request } = message;

          logger.debug("WS rcv:", message);

          if(answerTo) {
            const answer = this.handlers.get(answerTo);

            if(answer) {
              answer(request);
              this.handlers.delete(answerTo);
            }
          }

          if(doubleConnection) this.neverRetry = true;

          if(queue) {
            const { inQueue, matchId } = queue;

            if(inQueue !== undefined) this.emit("inQueue", inQueue);
            if(matchId) this.emit("match", matchId);
          }

          if(update) {
            const parsed: unknown = JSON.parse(update);

            if(! (typeof parsed === "object") || parsed === null) throw new Error("Invalid update");

            logger.debug("update:", parsed);
            this.emit("update", parsed);
          }
        } catch(error) {
          logger.error("While parsing message", event.data, error);
        }
      };

      ws.onopen = () => {
        this.status = "connected";
        this.ws = ws;
        setTimeout(() => this.emit("connection", true), 100);
        ws.send(WSRequest.stringify({ clientId: this.clientId }));
      };

      ws.onclose = () => {
        this.emit("connection", false);
        this.retry();
      };

      ws.onerror = error => {
        logger.error("WS error", error);
        ws.close();
      };
    } catch(error) {
      logger.error("While connecting to WS", error);
      this.retry();
    }
  }

  private retry() {
    if(this.neverRetry) this.status = "stop";
    else {
      this.status = "idle";
      setTimeout(() => this.connect(), 1000);
    }
  }

  isConnected() {
    return this.status === "connected";
  }

  /**
   * Sends a request through WS to server.
   *
   * @param request the request.
   * @param handler the response handles.
   * @returns `true` is sent, otherwise `false`.
   */
  request(request: WSRequest, handler?: (request: WSRequest) => void) {
    if(this.status !== "connected") return false;

    const requestId = ++this.requestNo;
    const body = { ...request, requestId };

    logger.debug("WS req:", body);
    this.ws!.send(RawWSRequest.stringify(body));

    if(handler) this.handlers.set(requestId, handler);

    return true;
  }
}

export const ws = new WS();
